import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../request';
import { URL } from "../api";
import getPrivilegeList from "./PrivilegeList";

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [status, setStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [hasNextPage, setHasNextPage] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [isRejectExporting, setIsRejectExporting] = useState(false);
  const [isPendingExporting, setIsPendingExporting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const privilegeList = getPrivilegeList();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [newComment, setNewComment] = useState({});
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectCustomer, setRejectCustomer] = useState(null);

  const statusMap = { 1: "Pending", 3: "Approved", 4: "Reject" };
  const reverseStatusMap = { Pending: 1, Approved: 3, Reject: 4 };
  const userId = localStorage.getItem("userId");
  const companyCode = localStorage.getItem("companyCode");
  const branchCode = localStorage.getItem("branch_code");

  const [branchMap, setBranchMap] = useState({});

  const openRejectModal = (customer) => {
    setRejectCustomer(customer);
    setShowRejectModal(true);
  };

  const fetchComments = async (customer_id) => {
    try {
      const res = await axiosInstance.get(URL.Comment_GET, { params: { customer_id } });
      const data = Array.isArray(res.data) ? res.data : [];
      setComments(data);
      setSelectedCustomerId(customer_id);
      setSelectedLoanId(data.length > 0 ? data[0].loan_id : null);
      setShowComments(true);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      setComments([]);
      setSelectedLoanId(null);
    }
  };

  const fetchCustomers = async (page = 1, filters = {}) => {
    try {
          const params = { ...filters };
          params.page = page - 1;
          params.size = rowsPerPage;

          // ✅ Add company/branch filter from localStorage
          if (companyCode) {
            params.company_code = companyCode;
          }

          if (companyCode && branchCode) {
            params.branch_code = branchCode;
          }

      const res = await axiosInstance.get(URL.GET_ALL, { params });
      const data = Array.isArray(res.data) ? res.data : [];

      setCustomers(data);
      setCurrentPage(page);
      setHasNextPage(data.length === rowsPerPage);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      setCustomers([]);
      setHasNextPage(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get("status");
    const filters = {};
    if (statusParam) {
      setStatus(statusParam);
      filters.status = reverseStatusMap[statusParam];
    }
    setActiveFilters(filters);
    fetchCustomers(1, filters);
  }, [location.search]);

  useEffect(() => {
    axiosInstance.get(URL.GET_ALL_BEANCHES).then((res) => {
      const map = {};
      const list = res.data?.data || [];
      list.forEach((b) => { map[String(b.branchCode)] = b.branchName; });
      setBranchMap(map);
    }).catch(() => {});
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleViewClick = (customer_id) => {
    navigate(`/CustomerView?customer_id=${customer_id}`);
  };

  const handleAddComment = async (customer) => {
    try {
      const commentPayload = {
        customer_id: customer.customer_id,
        comment: newComment[customer.customer_id],
        comment_by: Number(userId),
        bypass_flag: 1,
        comment_type: 1,
      };
      await axiosInstance.post(URL.COMMENT_ADD, commentPayload);
      setNewComment(prev => ({ ...prev, [customer.customer_id]: "" }));
      fetchComments(customer.customer_id);
    } catch (err) {
      console.error("Failed to add comment", err);
    }
  };

  const handleSearch = () => {
    const filters = {};
    if (filterType === "customer_id") filters.customer_id = filterValue;
    if (filterType === "pan_number") filters.pan_number = filterValue;
    if (filterType === "phone_number") filters.phone_number = filterValue;
    if (status) filters.status = reverseStatusMap[status];
    setActiveFilters(filters);
    fetchCustomers(1, filters);
  };

  // ✅ UPDATED: Call Backend for Excel
  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      const params = { ...activeFilters, export: true };

          if (companyCode) {
            params.company_code = companyCode;
          }

          if (companyCode && branchCode) {
            params.branch_code = branchCode;
          }
      
      const response = await axiosInstance.get(URL.GET_ALL, {
        params,
        responseType: "blob", 
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Customer_List_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel export failed", error);
      alert("Failed to export Excel.");
    } finally {
      setIsExporting(false);
    }
  };

  const exportPendingApproval = async () => {
    try {
      setIsPendingExporting(true);
      const response = await axiosInstance.get(URL.PENDING_APPROVAL_EXPORT, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Pending_Approval_Customers_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Pending approval export failed", error);
      alert("Failed to export Pending Approval Customers.");
    } finally {
      setIsPendingExporting(false);
    }
  };

  const exportRejectSummary = async () => {
    try {
      setIsRejectExporting(true);
      const response = await axiosInstance.get(URL.REJECT_COMMNET, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Reject_Comment_Summary.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Reject summary export failed", error);
    } finally {
      setIsRejectExporting(false);
    }
  };


  // cxvbn
  // 🔹 Use backend filtered customers directly
  const currentCustomers = customers;

  return (
    <div className="container">
      <h3 className="mb-4">All Customer List</h3>
      
      <div className="card mb-4 p-3 position-relative">
        <div
          className="d-flex flex-wrap gap-2 position-absolute"
          style={{ top: "10px", right: "10px" }}
        >
          <button
            className="btn btn-success btn-sm"
            onClick={exportToExcel}
            disabled={isExporting}
          >
            {isExporting ? "Processing..." : "Export Excel"}
          </button>

          <button
            className="btn btn-danger btn-sm"
            onClick={exportRejectSummary}
            disabled={isRejectExporting}
          >
            {isRejectExporting ? "Downloading..." : "Reject Summary Export"}
          </button>

          {privilegeList.pendingApprovalExportView && (
            <button
              className="btn btn-warning btn-sm"
              onClick={exportPendingApproval}
              disabled={isPendingExporting}
            >
              {isPendingExporting ? "Downloading..." : "Pending Approval Export"}
            </button>
          )}
        </div>

        <div className="row gx-2 gy-2 align-items-end">
          <div className="col-auto">
            <label className="form-label">Filter Type</label>
            <select
              className="form-select form-select-sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Select</option>
              <option value="customer_id">Customer ID</option>
              <option value="pan_number">PAN Number</option>
              <option value="phone_number">Phone Number</option>
            </select>
          </div>

          {filterType && (
            <div className="col-auto">
              <label className="form-label">
                {filterType === 'customer_id' ? 'Customer ID' : filterType === 'pan_number' ? 'PAN Number' : 'Phone Number'}
              </label>
              <input
                type={filterType === 'customer_id' ? 'number' : 'text'}
                className="form-control form-control-sm"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
          )}

          <div className="col-auto">
            <label className="form-label">Status</label>
            <select
              className="form-select form-select-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Reject">Reject</option>
              <option value="Approved">Approved</option>
            </select>
          </div>

          <div className="col-auto">
            <button className="btn btn-sm btn-primary mt-1" onClick={handleSearch}>
              Search
            </button>
          </div>
        </div>
      </div>

      <div className="table-responsive" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
        <table className="table tablestyle table-sm table-bordered table-hover align-middle">
          <thead className="table-dark">
            <tr>
              <th>S No.</th>
              <th>Cid</th>
              <th>Borrower Name</th>
              <th>Channel</th>
              <th>Agency</th>
              <th>Gender</th>
              <th>DOB</th>
              <th>Phone No.</th>
              <th>PAN Number</th>
              <th>Bureau Score</th> {/* ✅ ADDED */}
              <th>Pin Code</th>
              <th>Status</th>
              <th>Product Type</th>
              <th>Registration Date</th>
              {privilegeList.customerDetailsView && <th>Action</th>}
              <th>Reject Remarks</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer, index) => (
              <tr key={customer.customer_id || index}>
                <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                <td>{customer.customer_id || '-'}</td>
                <td>{customer.full_name || '-'}</td>
              <td>
                <span style={{ fontWeight: 600 }}>
                  {customer.company_code
                    ? "Finvolt"
                    : customer.agent_code === "GR7689G"
                      ? "BACHATT"
                      : customer.loan_amount
                        ? "Web"
                        : "Peday App"}
                </span>
              </td>
                <td>
                  {customer.branch_code && branchMap[String(customer.branch_code)]
                    ? branchMap[String(customer.branch_code)]
                    : "-"}
                </td>
                <td>{customer.gender || '-'}</td>
                <td>{customer.dob || '-'}</td>
                <td>{customer.phone_number || '-'}</td>
                <td>{customer.pan_number || '-'}</td>
                <td>{customer.bureau_score || '-'}</td> {/* ✅ ADDED */}
                <td>{customer.pin_code || '-'}</td>
                <td>{statusMap[customer.customer_status_id] || '-'}</td>
                <td>{customer.company_code ? 'Monthly' : customer.agent_code == null ? 'PD' : 'EDI'}</td>
                <td>{customer.created_date || '-'}</td>
                {privilegeList.customerDetailsView && (
                  <td>
                    <button className="btn btn-sm btn-primary me-1" onClick={() => handleViewClick(customer.customer_id)}>View</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => fetchComments(customer.customer_id)}>Comments</button>
                  </td>
                )}
                <td>
                  {[1, 4].includes(customer.customer_status_id) ? (
                    <button className="btn btn-sm btn-danger" onClick={() => openRejectModal(customer)}>Add Comment</button>
                  ) : "-"}
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr><td colSpan="16" className="text-center text-muted">No customers found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination & Modals (Rest of your code remains the same) */}
      <div className="d-flex justify-content-center mt-3">
        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => fetchCustomers(currentPage - 1, activeFilters)}
            disabled={currentPage === 1}
          > Prev </button>
          <span className="fw-semibold">Page {currentPage}</span>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => fetchCustomers(currentPage + 1, activeFilters)}
            disabled={!hasNextPage}
          > Next </button>
        </div>
      </div>

      {/* ... Comment and Reject Modals ... */}
      {showComments && (
        <>
          <div className="modal-backdrop fade show" style={{ backdropFilter: "blur(4px)" }}></div>
          <div className="modal d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Comments - CID: {selectedCustomerId}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowComments(false)}></button>
                </div>
                <div className="modal-body">
                  {comments.length > 0 ? comments.map((c) => (
                    <div key={c.id} className="border rounded p-2 mb-2 bg-light">
                      <div><strong>Date:</strong> {c.create_date} | <strong>By:</strong> {c.commented_by_username}</div>
                      <div><strong>Comment:</strong> {c.comment}</div>
                    </div>
                  )) : <p>No comments available.</p>}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showRejectModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal d-block" tabIndex="-1">
            <div className="modal-dialog modal-md modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Remark – CID: {rejectCustomer?.customer_id}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowRejectModal(false)}></button>
                </div>
                <div className="modal-body">
                  <textarea
                    className="form-control"
                    rows="4"
                    value={newComment[rejectCustomer?.customer_id] || ""}
                    onChange={(e) => setNewComment(prev => ({ ...prev, [rejectCustomer.customer_id]: e.target.value }))}
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Cancel</button>
                  <button className="btn btn-danger" disabled={!newComment[rejectCustomer?.customer_id]} onClick={() => { handleAddComment(rejectCustomer); setShowRejectModal(false); }}>Save Remark</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerList;